// src/modules/stock/ProductionOrderPage.js
// VERSION: 3.1 - With Notifications Integration

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { db } from '../../firebaseConfig';
import {
    addDoc, collection, getDocs, getDoc, updateDoc, doc, query, where,
    onSnapshot, serverTimestamp, runTransaction, writeBatch, increment,
    Timestamp, deleteDoc
} from 'firebase/firestore';
import useFirestore from '../../hooks/useFirestore';
import { useStockAggregator } from '../../hooks/useStockAggregator';
import { useAuxiliaryData } from '../../hooks/useAuxiliaryData';
import { useActivityLogger } from '../../hooks/useActivityLogger';
import { initializeStockAggregator } from '../../hooks/useStockAggregatorInit';
import usePagePermissions from '../../hooks/usePagePermissions';
import useAuth from '../../hooks/useAuth';
import styles from './Production.module.css';
import { exportProductionSummary, exportPendingOpsSummary, exportProcessoManipulacao, exportProcessoEnvase, exportProcessoControleQualidade } from '../../documents';

// Notification imports
import { useNotifications } from '../../contexts/NotificationContext';
import { getModuleConfig } from '../../config/notificationConfig';

// Reusable Components
import { Toast, EmptyState, ConfirmModal, TableSkeleton } from '../../components/common';

// Import centralized helpers
import { isWaterMaterial, getMaterialUnit, formatMaterialDisplay } from '../../utils/materialHelpers';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiFileText,
    FiDownload,
    FiSearch,
    FiPackage,
    FiClipboard,
    FiCalendar,
    FiTag,
    FiMapPin,
    FiList,
    FiEye,
    FiPlus,
    FiUpload,
    FiPrinter,
    FiChevronUp,
    FiChevronDown,
    FiClock,
    FiUser,
    FiBox,
    FiDroplet,
    FiFolder,
    FiHome,
    FiBriefcase,
    FiTruck,
    FiShoppingCart,
    FiDollarSign,
    FiPercent,
    FiCreditCard,
    FiSend,
    FiFlag,
    FiCheckCircle,
    FiXCircle,
    FiClock as FiClockIcon,
    FiTrendingUp,
    FiUsers,
    FiPhone,
    FiMail,
    FiMap,
    FiNavigation,
    FiCalendar as FiCalendarIcon,
    FiMessageSquare,
    FiSettings,
    FiStar,
    FiHeart,
    FiThumbsUp,
    FiThumbsDown,
    FiMoreHorizontal,
    FiAward,
    FiDollarSign as FiMoney,
    FiArrowLeft as FiArrowLeftIcon,
    FiArrowRight as FiArrowRightIcon,
    FiPlusCircle,
    FiMinusCircle,
    FiInbox,
    FiExternalLink,
    FiHelpCircle,
    FiAlertTriangle,
    FiCheckCircle as FiCheckCircleIcon,
    FiClock as FiClockIcon2,
    FiTag as FiTagIcon,
    FiFlag as FiFlagIcon,
    FiUsers as FiUsersIcon,
    FiUser as FiUserIcon,
    FiCalendar as FiCalendarIcon2,
    FiMessageSquare as FiMessageSquareIcon,
    FiSend as FiSendIcon,
    FiBarChart2,
    FiPieChart,
    FiFile,
    FiFolder as FiFolderIcon,
    FiTrash,
    FiEdit2,
    FiEye as FiEyeIcon,
    FiPlus as FiPlusIcon,
    FiMinus,
    FiXOctagon,
    FiCheckSquare,
    FiSquare,
    FiArrowUp,
    FiArrowDown,
    FiFilter,
    FiGrid,
    FiList as FiListIcon,
    FiMaximize2,
    FiMinimize2
} from 'react-icons/fi';
import {
    MdEdit,
    MdDelete,
    MdAdd,
    MdSave,
    MdCancel,
    MdVisibility,
    MdPrint,
    MdLocationOn,
    MdBusiness,
    MdPerson,
    MdPhone,
    MdEmail,
    MdAttachMoney,
    MdReceipt,
    MdLocalShipping,
    MdCheckCircle,
    MdCancel as MdCancelIcon,
    MdPending,
    MdApproval,
    MdProductionQuantityLimits,
    MdDeliveryDining,
    MdFactCheck,
    MdMoney,
    MdMonetizationOn,
    MdOutlineCheckCircle,
    MdOutlineCancel,
    MdOutlinePending,
    MdInventory,
    MdHelp,
    MdSupport,
    MdAssignment,
    MdPeople,
    MdPersonOutline,
    MdCategory,
    MdPriorityHigh,
    MdAccessTime,
    MdAssignmentInd,
    MdQrCode,
    MdPrint as MdPrintIcon,
    MdShowChart,
    MdTimeline,
    MdProductionQuantityLimits as MdProductionIcon,
    MdFactCheck as MdFactCheckIcon,
    MdOutlineProductionQuantityLimits
} from 'react-icons/md';
import { FaBox, FaTruck, FaUser, FaBuilding, FaMoneyBillWave, FaClipboardList, FaDollarSign, FaPrint, FaCheck, FaTimes, FaEye, FaMapMarkerAlt, FaBoxOpen, FaWarehouse, FaTicketAlt, FaClock, FaFlag, FaUserTag, FaUsers, FaTasks, FaBarcode, FaQrcode, FaChartLine, FaChartBar, FaFileExport, FaFileImport, FaPlusCircle, FaMinusCircle as FaMinusCircleIcon, FaTrashAlt, FaChevronRight } from 'react-icons/fa';

// ============================================================================
// CONSTANTS - Unified with useAuxiliaryData.js
// ============================================================================
const ITEMS_COLLECTION = 'items';
const PRODUCTS_COLLECTION = 'products';
const STOCK_OUTFLOW_MAP = {
    'current-stock': 'stock-outflows',
};
const STOCK_ENTRY_MAP = {
    'current-stock': 'stock-entries',
};
const PRODUCTION_ORDERS_COLLECTION = 'production-orders';

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ProductionOrderPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('ordens_de_producao');
    const { logActivity } = useActivityLogger();
    const { addNotification } = useNotifications();
    const { currentUser } = useAuth();

    const [productionOrders, setProductionOrders] = useState([]);
    const [tempItems, setTempItems] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [calcMode, setCalcMode] = useState('mass');
    const [inputValue, setInputValue] = useState('');

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [simulatingItem, setSimulatingItem] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmRows, setConfirmRows] = useState({});
    const [showDocumentsModal, setShowDocumentsModal] = useState(false);
    const [selectedOrderForDocs, setSelectedOrderForDocs] = useState(null);

    const [openSections, setOpenSections] = useState({ MP: true, EMB: true });
    const [isCancelling, setIsCancelling] = useState(false);
    const [activeOrders, setActiveOrders] = useState([]);
    const [isFinalizingOrder, setIsFinalizingOrder] = useState(false);
    const [orderStockStatus, setOrderStockStatus] = useState({});
    const [isInitializingAggregator, setIsInitializingAggregator] = useState(true);

    // Toast notifications state
    const [toasts, setToasts] = useState([]);
    const [confirmDialog, setConfirmDialog] = useState(null);
    const qtyInputRef = useRef(null);

    // Hooks otimizados
    const { stockTotals, isLoading: isLoadingStock, getStockForMaterial } = useStockAggregator();
    const {
        allItems,
        allRawMaterials,
        allCategories,
        allProductTypes,
        procedures,
        isLoading: isLoadingAux,
        isInitialized: auxInitialized
    } = useAuxiliaryData();
    const { data: products, isLoading: isLoadingProducts } = useFirestore(PRODUCTS_COLLECTION);

    // Toast notification helper
    const showToast = useCallback((message, type = 'info', duration = 5000) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type, duration }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(n => n.id !== id));
    }, []);

    const showConfirm = useCallback((message, onConfirm) => {
        setConfirmDialog({ message, onConfirm });
    }, []);

    const closeConfirm = useCallback(() => {
        setConfirmDialog(null);
    }, []);

    const toggleSection = (section) => {
        setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // ============================================================================
    // NOTIFICATION FUNCTIONS
    // ============================================================================

    /**
     * Notifica usuários sobre criação de ordem de produção
     */
    const notifyProductionOrderCreated = useCallback(async (orderData, orderId, opCode, productName, status, missingMaterials = []) => {
        try {
            // Buscar usuários com permissão de ordens de produção
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const targetUsers = [];

            usersSnapshot.forEach(doc => {
                const userData = doc.data();
                const roles = Array.isArray(userData.role) ? userData.role : [userData.role];
                const permissions = Array.isArray(userData.permissions) ? userData.permissions : [];

                if (roles.some(r => r === 'Diretoria' || r === 'Diretoria (Admin)' || r === 'Operacional' || r === 'Estoque') ||
                    permissions.includes('ordens_de_producao')) {
                    targetUsers.push({
                        uid: doc.id,
                        ...userData
                    });
                }
            });

            console.log(`📢 Enviando notificações de ordem de produção para ${targetUsers.length} usuários`);

            const config = getModuleConfig('ordens_de_producao');

            const hasStock = status === 'awaiting-start';
            const statusLabel = hasStock ? 'Aguardando Início' : 'Aguardando MP';
            const statusEmoji = hasStock ? '🔵' : '🟡';

            const baseNotification = {
                type: 'production_order_created',
                module: 'ordens_de_producao',
                moduleIcon: config?.icon || '🏭',
                moduleColor: config?.color || '#e67e22',
                moduleName: config?.name || 'Ordens de Produção',
                title: `${statusEmoji} Nova Ordem de Produção`,
                fullMessage: `Detalhes da OP:\n• OP: ${opCode}\n• Produto: ${productName}\n• Status: ${statusLabel}\n${!hasStock ? `• Materiais em falta: ${missingMaterials.length > 0 ? missingMaterials.map(m => m.name).join(', ') : 'Verificar estoque'}` : '• Estoque disponível para início'}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: hasStock ? 'Média' : 'Alta',
                route: `/producao/ordens-de-producao`,
                data: {
                    orderId: orderId,
                    opCode: opCode,
                    productName: productName,
                    status: status,
                    hasStock: hasStock,
                    missingMaterials: missingMaterials
                }
            };

            for (const user of targetUsers) {
                if (user.uid === currentUser?.uid) continue;

                const notification = {
                    ...baseNotification,
                    id: `po_${orderId}_${user.uid}_${Date.now()}`,
                    message: hasStock ?
                        `${user.nome || 'Usuário'}, a OP ${opCode} para ${productName} foi criada e está aguardando início` :
                        `${user.nome || 'Usuário'}, a OP ${opCode} para ${productName} foi criada mas está aguardando matérias-primas`,
                    userId: user.uid
                };

                addNotification(notification);

                try {
                    await addDoc(collection(db, 'notifications'), {
                        userId: user.uid,
                        ...notification,
                        createdAt: new Date()
                    });
                } catch (err) {
                    console.warn('Erro ao salvar notificação:', err);
                }
            }

            console.log(`✅ Notificações de OP enviadas para ${targetUsers.length} usuários`);
        } catch (error) {
            console.error('❌ Erro ao enviar notificações de OP:', error);
        }
    }, [currentUser, addNotification]);

    /**
     * Notifica sobre mudança de status da ordem de produção
     */
    const notifyProductionOrderStatusChange = useCallback(async (order, newStatus, previousStatus, updatedBy) => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const targetUsers = [];

            // Get status label
            const statusLabels = {
                'awaiting-mp': 'Aguardando MP',
                'awaiting-start': 'Aguardando Início',
                'pending-deduction': 'Dedução Pendente',
                'active': 'Em Andamento',
                'completed': 'Concluída'
            };
            const statusLabel = statusLabels[newStatus] || newStatus;

            // Find creator or responsible user
            let creatorUser = null;
            if (order.createdBy) {
                usersSnapshot.forEach(doc => {
                    const userData = doc.data();
                    if (userData.nome === order.createdBy || userData.email === order.createdBy) {
                        creatorUser = { uid: doc.id, ...userData };
                    }
                });
            }

            usersSnapshot.forEach(doc => {
                const userData = doc.data();
                const roles = Array.isArray(userData.role) ? userData.role : [userData.role];

                if (roles.some(r => r === 'Diretoria' || r === 'Diretoria (Admin)' || r === 'Operacional' || r === 'Estoque')) {
                    targetUsers.push({
                        uid: doc.id,
                        ...userData
                    });
                }
            });

            // Add creator if not already included
            if (creatorUser && !targetUsers.some(u => u.uid === creatorUser.uid)) {
                targetUsers.push(creatorUser);
            }

            const config = getModuleConfig('ordens_de_producao');

            let emoji, title, priority;
            if (newStatus === 'active') {
                emoji = '▶️';
                title = 'Ordem de Produção Iniciada';
                priority = 'Alta';
            } else if (newStatus === 'completed') {
                emoji = '✅';
                title = 'Ordem de Produção Concluída';
                priority = 'Alta';
            } else if (newStatus === 'awaiting-start') {
                emoji = '🔵';
                title = 'OP Pronta para Iniciar';
                priority = 'Média';
            } else if (newStatus === 'awaiting-mp') {
                emoji = '🟡';
                title = 'OP Aguardando Materiais';
                priority = 'Alta';
            } else {
                emoji = '📋';
                title = `Status da OP: ${statusLabel}`;
                priority = 'Média';
            }

            const baseNotification = {
                type: 'production_order_status_change',
                module: 'ordens_de_producao',
                moduleIcon: config?.icon || '🏭',
                moduleColor: config?.color || '#e67e22',
                moduleName: config?.name || 'Ordens de Produção',
                title: `${emoji} ${title}`,
                fullMessage: `Detalhes:\n• OP: ${order.opCode}\n• Produto: ${order.items?.[0]?.productName || 'N/A'}\n• Status anterior: ${statusLabels[previousStatus] || previousStatus}\n• Novo status: ${statusLabel}\n• Atualizado por: ${updatedBy || 'Sistema'}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: priority,
                route: `/producao/ordens-de-producao`,
                data: {
                    orderId: order.id,
                    opCode: order.opCode,
                    previousStatus: previousStatus,
                    newStatus: newStatus,
                    statusLabel: statusLabel,
                    updatedBy: updatedBy || 'Sistema'
                }
            };

            for (const user of targetUsers) {
                if (user.uid === currentUser?.uid) continue;

                const notification = {
                    ...baseNotification,
                    id: `po_status_${order.id}_${user.uid}_${Date.now()}`,
                    message: `${user.nome || 'Usuário'}, a OP ${order.opCode} mudou de status para ${statusLabel}`,
                    userId: user.uid
                };

                addNotification(notification);

                try {
                    await addDoc(collection(db, 'notifications'), {
                        userId: user.uid,
                        ...notification,
                        createdAt: new Date()
                    });
                } catch (err) {
                    console.warn('Erro ao salvar notificação:', err);
                }
            }
        } catch (error) {
            console.error('❌ Erro ao notificar mudança de status da OP:', error);
        }
    }, [currentUser, addNotification]);

    // ============================================================================
    // Inicializar agregador de estoque
    // ============================================================================
    useEffect(() => {
        const init = async () => {
            setIsInitializingAggregator(true);
            await initializeStockAggregator(showToast);
            setIsInitializingAggregator(false);
        };
        init();
    }, [showToast]);

    // ============================================================================
    // REAL-TIME LISTENER FOR ACTIVE ORDERS
    // ============================================================================
    useEffect(() => {
        let unsubscribe;
        let reconnectTimeout;
        let retryCount = 0;

        const setupListener = () => {
            if (unsubscribe) {
                unsubscribe();
            }

            unsubscribe = onSnapshot(
                collection(db, PRODUCTION_ORDERS_COLLECTION),
                (snap) => {
                    try {
                        const orders = snap.docs
                            .map(d => {
                                const data = d.data();
                                if (!data.opCode || !data.items) {
                                    console.warn('Dados de ordem de produção incompletos:', d.id);
                                    return null;
                                }
                                return { id: d.id, ...data };
                            })
                            .filter(o => o !== null)
                            .filter(o => o.status !== 'completed')
                            .sort((a, b) => {
                                const ta = a.createdAt?.toMillis?.() || 0;
                                const tb = b.createdAt?.toMillis?.() || 0;
                                return tb - ta;
                            });

                        setActiveOrders(orders);
                        retryCount = 0;
                    } catch (error) {
                        console.error("Error processing orders snapshot:", error);
                    }
                },
                (error) => {
                    console.error("Listener error:", error);
                    retryCount++;

                    if (retryCount < 5) {
                        const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
                        reconnectTimeout = setTimeout(() => {
                            setupListener();
                        }, delay);
                    } else {
                        showToast(
                            t('pages.productionOrder.errors.connectionLost'),
                            'error',
                            0
                        );
                    }
                }
            );
        };

        setupListener();

        return () => {
            if (unsubscribe) unsubscribe();
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
        };
    }, [showToast, t]);

    // ============================================================================
    // PRODUCT OPTIONS - FIXED: Ensure id is always present
    // ============================================================================
    const productOptions = useMemo(() => {
        if (!products) return [];
        return products.map(p => ({
            value: p.id,
            label: `${p.code} - ${p.commercialName}`,
            id: p.id,
            productId: p.id,
            ...p
        })).sort((a, b) => a.label.localeCompare(b.label));
    }, [products]);

    // ============================================================================
    // DETERMINE IF MATERIAL IS PACKAGING (UNIT-BASED)
    // ============================================================================
    const isPackagingMaterial = useCallback((materialId, fallbackData = null) => {
        const material = allItems.find(item => item.id === materialId) || fallbackData;
        if (!material) return false;

        const materialCategories = material.categoryIds?.map(catId =>
            allCategories?.find(c => c.id === catId)?.name?.toUpperCase() || ''
        ) || [];

        const materialTypes = material.productTypeIds?.map(typeId =>
            allProductTypes?.find(t => t.id === typeId)?.name?.toUpperCase() || ''
        ) || [];

        const isProduct = materialCategories.some(name => name.includes('ACABADO')) ||
            materialTypes.some(name => name.includes('ACABADO'));

        if (isProduct) return false;

        const packagingTerms = ['EMBALAGEM', 'SERIGRAFIA', 'FRASCO', 'TAMPA', 'ROTULO', 'CAIXA', 'CARTUCHO'];

        const isPackaging = materialCategories.some(name => packagingTerms.some(term => name.includes(term))) ||
            materialTypes.some(name => packagingTerms.some(term => name.includes(term)));

        return isPackaging;
    }, [allItems, allCategories, allProductTypes]);

    // ============================================================================
    // SIMULATION ENGINE - FIXED: Always return valid arrays
    // ============================================================================
    const getSimulation = useCallback((item) => {
        const value = parseFloat(item.requestedValue) || 0;
        const materials = item.rawMaterials || [];

        const lossMultiplier = parseFloat(item.lossIndex) || 1;

        const gramaturaRaw = parseFloat(item.gramatura) || 0;
        const gramaturaBase = (item.gramaturaUnit === 'g' || item.gramaturaUnit === 'ml')
            ? gramaturaRaw / 1000
            : gramaturaRaw;

        const totalMass = item.mode === 'units' ? value * gramaturaBase : value;

        const massItems = [];
        const unitItems = [];

        materials.forEach(mp => {
            if (!mp.id) return;

            const mpData = allItems.find(m => m.id === mp.id);
            const isUnitary = isPackagingMaterial(mp.id, mp);
            const quantityInFormula = parseFloat(mp.quantity) || 0;

            const isWater = isWaterMaterial(mpData);
            const stockActual = isWater ? Infinity : getStockForMaterial(mp.id);
            const materialName = formatMaterialDisplay(mpData);

            if (isUnitary) {
                const needed = value * quantityInFormula;
                unitItems.push({
                    ...mp,
                    needed: needed,
                    unit: 'Un',
                    stockActual,
                    displayName: materialName,
                    isWater
                });
            } else {
                const baseNeeded = totalMass * quantityInFormula / 100;
                const finalNeededWithLoss = baseNeeded * lossMultiplier;
                massItems.push({
                    ...mp,
                    needed: finalNeededWithLoss,
                    unit: getMaterialUnit(mpData, allCategories) || 'Kg',
                    stockActual,
                    displayName: materialName,
                    isWater
                });
            }
        });

        return {
            massItems: massItems || [],
            unitItems: unitItems || [],
            totalMass: totalMass || 0
        };
    }, [allItems, allCategories, allProductTypes, isPackagingMaterial, getStockForMaterial]);

    // ============================================================================
    // CHECK INDIVIDUAL ORDER STOCK STATUS
    // ============================================================================
    const checkOrderStockStatus = useCallback((item) => {
        const sim = getSimulation(item);
        const allMaterials = [...sim.massItems, ...sim.unitItems];
        const nonWaterMaterials = allMaterials.filter(m => !m.isWater);

        const missingMaterials = nonWaterMaterials
            .filter(m => m.needed > m.stockActual)
            .map(m => ({
                id: m.id,
                name: m.displayName || m.inciName || m.label,
                needed: m.needed,
                available: m.stockActual,
                unit: m.unit
            }));

        const hasSufficientStock = missingMaterials.length === 0;

        return {
            hasSufficientStock,
            missingMaterials,
            totalMaterials: nonWaterMaterials.length
        };
    }, [getSimulation]);

    // ============================================================================
    // UPDATE STOCK STATUS FOR ALL TEMP ITEMS
    // ============================================================================
    useEffect(() => {
        if (tempItems.length > 0) {
            const statusMap = {};
            tempItems.forEach(item => {
                statusMap[item.tempId] = checkOrderStockStatus(item);
            });
            setOrderStockStatus(statusMap);
        } else {
            setOrderStockStatus({});
        }
    }, [tempItems, checkOrderStockStatus, stockTotals]);

    // ============================================================================
    // GLOBAL STOCK VALIDATION
    // ============================================================================
    const globalValidation = useMemo(() => {
        const totals = {};
        tempItems.forEach(item => {
            const sim = getSimulation(item);
            [...sim.massItems, ...sim.unitItems].forEach(m => {
                if (!totals[m.id]) {
                    totals[m.id] = {
                        id: m.id,
                        name: m.displayName || m.inciName || m.label,
                        needed: 0,
                        stock: m.stockActual,
                        unit: m.unit,
                        isWater: m.isWater
                    };
                }
                totals[m.id].needed += m.needed;
            });
        });
        const missingItems = Object.values(totals).filter(t => t.needed > t.stock);
        return { hasError: missingItems.length > 0, missingItems, totals };
    }, [tempItems, getSimulation]);

    // ============================================================================
    // GROUPED MATERIALS
    // ============================================================================
    const groupedMaterials = useMemo(() => {
        const categories = {
            MP: {
                label: t('pages.productionOrder.rawMaterials'),
                items: []
            },
            EMB: {
                label: t('pages.productionOrder.packaging'),
                items: []
            }
        };

        Object.values(globalValidation.totals).forEach(mat => {
            const isUnit = isPackagingMaterial(mat.id, mat);
            if (isUnit) {
                categories.EMB.items.push(mat);
            } else {
                categories.MP.items.push(mat);
            }
        });

        return categories;
    }, [globalValidation.totals, isPackagingMaterial, t]);

    // ============================================================================
    // GENERATE ATOMIC OP CODE
    // ============================================================================
    const generateAtomicOpCode = useCallback(async () => {
        return await runTransaction(db, async (transaction) => {
            const currentYear = new Date().getFullYear();
            const allOpsRef = collection(db, PRODUCTION_ORDERS_COLLECTION);
            const allOpsSnap = await getDocs(allOpsRef);

            const thisYearOps = allOpsSnap.docs.filter(d => {
                const code = d.data().opCode || '';
                return code.startsWith(`OP${currentYear}.`);
            });

            const nextNum = thisYearOps.length + 1;
            const opCode = `OP${currentYear}.${String(nextNum).padStart(5, '0')}`;

            const existingOrder = thisYearOps.find(o => o.data().opCode === opCode);
            if (existingOrder) {
                throw new Error(`OP code collision detected: ${opCode}`);
            }

            return opCode;
        });
    }, []);

    // ============================================================================
    // EXPORT FUNCTIONS
    // ============================================================================
    const handleExportToExcel = async () => {
        await exportProductionSummary({
            globalValidation,
            allRawMaterials,
            allCategories,
            products
        });
    };

    const handleExportPendingOps = async () => {
        await exportPendingOpsSummary({
            products,
            allRawMaterials,
            allCategories,
            getSimulation,
            showToast
        });
    };

    const openDocumentsModal = (order) => {
        setSelectedOrderForDocs(order);
        setShowDocumentsModal(true);
    };

    const handleDocumentExport = async (documentType) => {
        if (!selectedOrderForDocs) return;

        switch (documentType) {
            case 'processo-manipulacao':
                await handleExportProcessoManipulacao(selectedOrderForDocs);
                break;
            case 'ficha-pesagem':
                await handleExportProcessoEnvase(selectedOrderForDocs);
                break;
            case 'processo-controle-qualidade':
                await handleExportProcessoControleQualidade(selectedOrderForDocs);
                break;
            case 'ordem-producao':
                showToast(t('pages.productionOrder.documents.orderProductionSoon'), 'info');
                break;
            case 'rotulagem':
                showToast(t('pages.productionOrder.documents.labelingSoon'), 'info');
                break;
            default:
                showToast(t('pages.productionOrder.documents.notRecognized'), 'warning');
        }

        setShowDocumentsModal(false);
    };

    const handleExportProcessoManipulacao = async (order) => {
        await exportProcessoManipulacao({
            order,
            allRawMaterials,
            allCategories,
            allItems,
            allProducts: products,
            allProductTypes,
            showToast
        });
    };

    const handleExportProcessoEnvase = async (order) => {
        await exportProcessoEnvase({
            order,
            allRawMaterials,
            allCategories,
            allProductTypes,
            showToast
        });
    };

    const handleExportProcessoControleQualidade = async (order) => {
        await exportProcessoControleQualidade({
            order,
            allRawMaterials,
            allCategories,
            allProductTypes,
            showToast
        });
    };

    // ============================================================================
    // VALIDATE ORDER DATA BEFORE SAVING
    // ============================================================================
    const validateOrderData = (orderData, item) => {
        const errors = [];

        if (!orderData.opCode) errors.push(t('pages.productionOrder.orderData.opCodeMissing'));
        if (!orderData.items || orderData.items.length === 0) errors.push(t('pages.productionOrder.orderData.itemsEmpty'));
        if (orderData.items && orderData.items[0]) {
            if (!orderData.items[0].productId) errors.push(t('pages.productionOrder.orderData.productIdMissing'));
            if (!orderData.items[0].productCode) errors.push(t('pages.productionOrder.orderData.productCodeMissing'));
            if (!orderData.items[0].productName) errors.push(t('pages.productionOrder.orderData.productNameMissing'));
            if (isNaN(orderData.items[0].quantity)) errors.push(t('pages.productionOrder.orderData.quantityInvalid'));
        }

        if (errors.length > 0) {
            console.error('Validation errors for item:', item.commercialName, errors);
            throw new Error(`${t('pages.productionOrder.orderData.invalidDataFor')} ${item.commercialName}: ${errors.join(', ')}`);
        }

        return true;
    };

    // ============================================================================
    // ADD ITEM TO TEMP ORDER
    // ============================================================================
    const addItemToTempOrder = () => {
        if (!selectedProduct || !inputValue) return;
        const qty = parseFloat(inputValue);
        if (isNaN(qty) || qty <= 0) {
            showToast(t('pages.productionOrder.validation.enterValidQuantity'), 'warning');
            return;
        }

        if (!selectedProduct.id && !selectedProduct.value) {
            showToast(t('pages.productionOrder.validation.productInvalidId'), 'error');
            return;
        }

        setTempItems(prev => [...prev, {
            ...selectedProduct,
            id: selectedProduct.id || selectedProduct.value,
            productId: selectedProduct.id || selectedProduct.value,
            requestedValue: qty,
            mode: calcMode,
            tempId: Date.now()
        }]);
        setSelectedProduct(null);
        setInputValue('');
    };

    const handleQtyKeyDown = (e) => {
        if (e.key === 'Enter') addItemToTempOrder();
    };

    const handleProductSelect = (option) => {
        setSelectedProduct(option);
        setTimeout(() => qtyInputRef.current?.focus(), 50);
    };

    // ============================================================================
    // GENERATE LOTE NUMBER
    // ============================================================================
    const generateLote = useCallback(async (productCode) => {
        try {
            const currentYear = new Date().getFullYear();
            const opsSnap = await getDocs(collection(db, PRODUCTION_ORDERS_COLLECTION));

            let maxNumber = 0;

            opsSnap.docs.forEach(d => {
                const orderData = d.data();
                (orderData.items || []).forEach(item => {
                    if (item.productCode === productCode && item.lote) {
                        const regex = new RegExp(`${productCode}(\\d{2})${currentYear}`);
                        const match = item.lote.match(regex);
                        if (match) {
                            const num = parseInt(match[1], 10);
                            if (num > maxNumber) maxNumber = num;
                        }
                    }
                });
            });

            const nextNumber = (maxNumber + 1).toString().padStart(2, '0');
            return `${productCode}${nextNumber}${currentYear}`;
        } catch (error) {
            console.error("Error generating lote:", error);
            const currentYear = new Date().getFullYear();
            return `${productCode}01${currentYear}`;
        }
    }, []);

    // ============================================================================
    // OPEN CONFIRMATION MODAL
    // ============================================================================
    const openConfirmModal = async () => {
        if (tempItems.length === 0) return;

        const initial = {};
        for (const item of tempItems) {
            const autoLote = await generateLote(item.code);
            initial[item.tempId] = {
                obs: '',
                waterPct: '',
                procedureId: '',
                lote: autoLote,
                etapa: '',
            };
        }

        setConfirmRows(initial);
        setShowConfirmModal(true);
    };

    const handleConfirmRowChange = (tempId, field, value) => {
        setConfirmRows(prev => ({
            ...prev,
            [tempId]: { ...prev[tempId], [field]: value }
        }));
    };

    // ============================================================================
    // FINALIZE ORDER - WITH NOTIFICATIONS
    // ============================================================================
    const finalizeOrder = async () => {
        const missingLote = tempItems.some(i => !confirmRows[i.tempId]?.lote?.trim());
        if (missingLote) {
            showToast(t('pages.productionOrder.validation.enterBatchForAll'), 'warning');
            return;
        }

        setIsFinalizingOrder(true);
        const createdOrders = [];
        const errors = [];

        try {
            for (const item of tempItems) {
                try {
                    if (!item.id && !item.productId && !item.value) {
                        throw new Error(`${t('pages.productionOrder.orderData.invalidDataFor')} ${item.commercialName}`);
                    }

                    const stockStatus = orderStockStatus[item.tempId];
                    const hasSufficientStock = stockStatus?.hasSufficientStock || false;

                    const opCode = await generateAtomicOpCode();
                    const sim = getSimulation(item);
                    const allSimItems = [...sim.massItems, ...sim.unitItems];

                    const materialMap = {};
                    allSimItems.forEach(m => {
                        if (!materialMap[m.id] && m.id) {
                            materialMap[m.id] = {
                                id: m.id,
                                needed: 0,
                                isWater: m.isWater || false,
                                unit: m.unit,
                                name: m.displayName || m.inciName || m.label || m.id
                            };
                        }
                        if (materialMap[m.id]) {
                            materialMap[m.id].needed += m.needed;
                        }
                    });

                    const materialArray = Object.values(materialMap).filter(m => m.needed > 0.0001 && m.id);

                    const productId = item.value || item.id || item.productId;
                    if (!productId) {
                        throw new Error(`${t('pages.productionOrder.orderData.invalidDataFor')} ${item.commercialName}`);
                    }

                    const finalItem = {
                        productId: productId,
                        productCode: item.code || '',
                        productName: item.commercialName || '',
                        quantity: parseFloat(item.requestedValue) || 0,
                        mode: item.mode || 'mass',
                        obs: confirmRows[item.tempId]?.obs?.trim() || '',
                        lote: confirmRows[item.tempId]?.lote?.trim() || '',
                        procedureId: confirmRows[item.tempId]?.procedureId || '',
                        waterPct: parseFloat(confirmRows[item.tempId]?.waterPct) || 0,
                        etapa: confirmRows[item.tempId]?.etapa?.trim() || '',
                    };

                    const initialStatus = hasSufficientStock ? 'awaiting-start' : 'awaiting-mp';

                    const orderData = {
                        opCode,
                        items: [finalItem],
                        materialsUsed: materialArray
                            .filter(m => !m.isWater && m.id && m.name)
                            .map(m => ({
                                id: m.id,
                                name: m.name,
                                needed: parseFloat(Number(m.needed).toFixed(4)),
                                unit: m.unit || 'Kg',
                            })),
                        materialsRequired: materialArray
                            .filter(m => m.id && m.name)
                            .map(m => ({
                                id: m.id,
                                name: m.name,
                                needed: parseFloat(Number(m.needed).toFixed(4)),
                                unit: m.unit || (m.isWater ? 'L' : 'Kg'),
                                isWater: m.isWater || false,
                            })),
                        status: initialStatus,
                        createdAt: serverTimestamp(),
                        version: 1,
                        createdBy: currentUser?.nome || 'Sistema',
                    };

                    if (!hasSufficientStock && stockStatus?.missingMaterials) {
                        orderData.missingMaterials = stockStatus.missingMaterials;
                    }

                    validateOrderData(orderData, item);

                    Object.keys(orderData).forEach(key => {
                        if (orderData[key] === undefined) {
                            delete orderData[key];
                        }
                    });

                    const orderRef = await addDoc(collection(db, PRODUCTION_ORDERS_COLLECTION), orderData);

                    // ✅ NOTIFICAR SOBRE CRIAÇÃO DA OP
                    await notifyProductionOrderCreated(
                        orderData,
                        orderRef.id,
                        opCode,
                        item.commercialName,
                        initialStatus,
                        stockStatus?.missingMaterials || []
                    );

                    await logActivity(
                        'order_created',
                        `production-order/${orderRef.id}`,
                        `OP ${opCode} criado para ${item.commercialName} (${item.code}) - Status: ${initialStatus}`
                    );

                    createdOrders.push({
                        id: orderRef.id,
                        opCode,
                        productName: item.commercialName,
                        status: initialStatus,
                        hasStock: hasSufficientStock,
                        missingMaterials: !hasSufficientStock ? stockStatus?.missingMaterials || [] : []
                    });

                    const statusMessage = hasSufficientStock
                        ? t('pages.productionOrder.statusMessages.createdAwaitingStart')
                        : t('pages.productionOrder.statusMessages.createdAwaitingMP');

                    showToast(
                        `${t('pages.productionOrder.statusMessages.op')} ${opCode} ${t('pages.productionOrder.statusMessages.for')} ${item.commercialName} ${statusMessage}.`,
                        hasSufficientStock ? 'info' : 'warning',
                        4000
                    );

                } catch (itemError) {
                    console.error(`Error creating OP for ${item.commercialName}:`, itemError);
                    errors.push(`${item.commercialName}: ${itemError.message}`);

                    showToast(
                        `${t('pages.productionOrder.statusMessages.errorCreatingFor')} ${item.commercialName}: ${itemError.message}`,
                        'error',
                        7000
                    );
                }
            }

            if (createdOrders.length > 0) {
                const awaitingStartList = createdOrders.filter(o => o.status === 'awaiting-start');
                const awaitingMpList = createdOrders.filter(o => o.status === 'awaiting-mp');

                let summaryMessage = `${createdOrders.length} ${t('pages.productionOrder.statusMessages.opsProcessed')}\n`;
                if (awaitingStartList.length > 0) {
                    summaryMessage += `🔵 ${t('pages.productionOrder.statusMessages.awaitingStart')} (${awaitingStartList.length}): ${awaitingStartList.map(o => o.opCode).join(', ')}\n`;
                }
                if (awaitingMpList.length > 0) {
                    summaryMessage += `🟡 ${t('pages.productionOrder.statusMessages.awaitingMPs')} (${awaitingMpList.length}): ${awaitingMpList.map(o => o.opCode).join(', ')}`;
                    if (awaitingMpList.some(o => o.missingMaterials?.length)) {
                        summaryMessage += `\n⚠️ ${t('pages.productionOrder.statusMessages.checkMissingMaterials')}`;
                    }
                }

                showToast(summaryMessage,
                    awaitingMpList.length > 0 ? 'warning' : 'info',
                    8000
                );
            }

            if (errors.length > 0) {
                showToast(
                    `${errors.length} ${t('pages.productionOrder.statusMessages.errorsFound')}`,
                    'error',
                    0
                );
            }

            setShowConfirmModal(false);
            setShowCreateModal(false);
            setTempItems([]);
            setConfirmRows({});
            setOrderStockStatus({});

        } catch (error) {
            console.error("Error in batch order creation:", error);
            showToast(
                `${t('pages.productionOrder.statusMessages.criticalError')} ${error.message}`,
                'error',
                0
            );
        } finally {
            setIsFinalizingOrder(false);
        }
    };

    // ============================================================================
    // DELETE ORDER
    // ============================================================================
    const deleteOrder = async (orderId) => {
        if (isCancelling) return;

        setIsCancelling(true);

        try {
            const orderRef = doc(db, PRODUCTION_ORDERS_COLLECTION, orderId);
            const orderSnap = await getDoc(orderRef);

            if (!orderSnap.exists()) throw new Error("Order not found");

            const orderData = orderSnap.data();
            const opCode = orderData.opCode || orderId;

            if (orderData.status === 'active' && orderData.deductionLog && orderData.deductionLog.length > 0) {
                const batch = writeBatch(db);
                const log = orderData.deductionLog || [];

                for (const entry of log) {
                    const stockDocRef = doc(db, entry.stockCollection, entry.stockDocId);
                    const stockSnap = await getDoc(stockDocRef);
                    const currentQty = stockSnap.exists() ? (parseFloat(stockSnap.data().quantity) || 0) : 0;
                    const newQty = parseFloat((currentQty + entry.quantity).toFixed(4));
                    batch.update(stockDocRef, { quantity: newQty });

                    const entryCollection = STOCK_ENTRY_MAP[entry.stockCollection];
                    if (entryCollection) {
                        const newLogRef = doc(collection(db, entryCollection));
                        batch.set(newLogRef, {
                            rawMaterialId: entry.materialId,
                            rawMaterialName: entry.materialName,
                            quantity: entry.quantity,
                            locationId: entry.location || null,
                            locationDescription: entry.locationDescription || null,
                            type: 'ENTRADA',
                            notes: `Deletion of OP ${opCode}`,
                            createdAt: serverTimestamp(),
                        });
                    }
                }

                batch.delete(orderRef);
                await batch.commit();

                showToast(t('pages.productionOrder.statusMessages.deleteSuccessRevert'), 'success');
            } else {
                await deleteDoc(orderRef);
                showToast(t('pages.productionOrder.statusMessages.deleteSuccess'), 'success');
                await logActivity(
                    'order_deleted',
                    `production-order/${orderId}`,
                    `OP ${opCode} removida${orderData.status === 'active' ? ' com reversão de estoque' : ''}`
                );
            }

        } catch (error) {
            console.error("Error deleting order:", error);
            showToast(t('pages.productionOrder.statusMessages.deleteError'), 'error');
        } finally {
            setIsCancelling(false);
        }
    };

    // ============================================================================
    // FORMAT DATE
    // ============================================================================
    const formatOrderDate = (timestamp) => {
        if (!timestamp?.toDate) return '—';
        return timestamp.toDate().toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // ============================================================================
    // GET STATUS BADGE
    // ============================================================================
    const getStatusBadge = (status) => {
        switch (status) {
            case 'awaiting-mp':
                return {
                    className: styles.statusBadgePending,
                    label: t('pages.productionOrder.statusAwaitingMP'),
                    style: { backgroundColor: '#f39c12', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem' }
                };
            case 'awaiting-start':
                return {
                    className: styles.statusBadgePending,
                    label: t('pages.productionOrder.statusAwaitingStart'),
                    style: { backgroundColor: '#3498db', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem' }
                };
            case 'pending-deduction':
                return {
                    className: styles.statusBadgePending,
                    label: t('pages.productionOrder.statusPendingDeduction'),
                    style: { backgroundColor: '#9b59b6', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem' }
                };
            case 'active':
                return {
                    className: styles.statusBadgeActive,
                    label: t('pages.productionOrder.statusActive'),
                    style: { backgroundColor: '#27ae60', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem' }
                };
            case 'completed':
                return {
                    className: styles.statusBadgeCompleted,
                    label: t('pages.productionOrder.statusCompleted'),
                    style: { backgroundColor: '#2ecc71', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem' }
                };
            default:
                return {
                    className: '',
                    label: status || t('pages.productionOrder.statusUnknown'),
                    style: { backgroundColor: '#95a5a6', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem' }
                };
        }
    };

    // ============================================================================
    // PROCEDURE OPTIONS
    // ============================================================================
    const procedureOptions = useMemo(() => {
        return procedures
            .filter(p => p.name)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [procedures]);

    // ============================================================================
    // RENDER - Water Material Row Styling Helper
    // ============================================================================
    const getMaterialRowClass = (isWater) => {
        return isWater ? styles.waterRow : '';
    };

    // Verificar se está carregando
    const isPageLoading = isInitializingAggregator || isLoadingProducts || isLoadingAux;

    // ============================================================================
    // RENDER
    // ============================================================================
    return (
        <div className={styles.pageContainer}>

            {/* Loading overlay */}
            {(isLoadingStock || isPageLoading) && (
                <div style={{
                    position: 'fixed',
                    top: '10px',
                    right: '10px',
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    zIndex: 10000,
                    fontSize: '0.9rem'
                }}>
                    <FiRefreshCw size={16} className={styles.buttonSpinner} style={{ marginRight: '8px' }} />
                    {t('pages.productionOrder.loadingData')}
                </div>
            )}

            {/* Toast Notifications - Using reusable Toast component */}
            <div className={styles.toastContainer}>
                {toasts.map(n => (
                    <Toast
                        key={n.id}
                        message={n.message}
                        type={n.type}
                        duration={n.duration}
                        onClose={() => removeToast(n.id)}
                    />
                ))}
            </div>

            {/* Confirm Dialog - Using reusable ConfirmModal component */}
            {confirmDialog && (
                <ConfirmModal
                    isOpen={true}
                    onClose={closeConfirm}
                    onConfirm={() => {
                        confirmDialog.onConfirm();
                        closeConfirm();
                    }}
                    title={t('common.confirm')}
                    message={confirmDialog.message}
                    confirmText={t('common.confirm')}
                    cancelText={t('common.cancel')}
                    danger={true}
                />
            )}

            {/* Page Header */}
            <header className={styles.header}>
                <h1 className={styles.headerTitle}>{t('pages.productionOrder.title')}</h1>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {can.create && (
                        <button
                            className={styles.addButton}
                            onClick={() => {
                                setTempItems([]);
                                setShowCreateModal(true);
                            }}
                            disabled={isPageLoading}
                        >
                            <FiPlus size={18} /> {t('pages.productionOrder.newOrder')}
                        </button>
                    )}
                    {can.export && (
                        <button
                            className={styles.exportButton}
                            onClick={handleExportPendingOps}
                            title={t('pages.productionOrder.exportPending')}
                            disabled={isPageLoading}
                        >
                            <FiFileText size={18} /> {t('pages.productionOrder.exportPending')}
                        </button>
                    )}
                </div>
            </header>

            {/* Active Orders Table */}
            <div>

                <div className={styles.tableContainer}>
                    {isPageLoading ? (
                        <TableSkeleton rows={3} columns={7} />
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t('pages.productionOrder.tableOP')}</th>
                                    <th>{t('pages.productionOrder.tableDateTime')}</th>
                                    <th>{t('pages.productionOrder.tableProducts')}</th>
                                    <th>{t('pages.productionOrder.tableBatch')}</th>
                                    <th style={{ textAlign: 'center', width: '100px' }}>{t('pages.productionOrder.tableStatus')}</th>
                                    <th style={{ textAlign: 'center', width: '10px' }}>{t('pages.productionOrder.tableActions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className={styles.emptyTable}>
                                            <EmptyState
                                                title={t('pages.productionOrder.noActiveOrders')}
                                                message={t('pages.productionOrder.noActiveOrdersMessage')}
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    activeOrders.map(order => {
                                        const statusInfo = getStatusBadge(order.status);
                                        return (
                                            <tr key={order.id} className={styles.tableRow}>
                                                <td><strong>{order.opCode || '—'}</strong></td>
                                                <td>{formatOrderDate(order.createdAt)}</td>
                                                <td>
                                                    {order.items?.map((item, i) => (
                                                        <div key={i}>
                                                            <strong>{item.productCode}</strong>
                                                            {' – '}{item.productName}
                                                            <small style={{ color: 'var(--secondary-color)', marginLeft: '4px' }}>
                                                                ({item.quantity} {item.mode === 'mass' ? 'Kg' : 'Un'})
                                                            </small>
                                                        </div>
                                                    ))}
                                                </td>
                                                <td>
                                                    {order.items?.map((item, i) => (
                                                        <div key={i}>
                                                            {item.lote || <span style={{ color: 'var(--secondary-color)', fontStyle: 'italic' }}>—</span>}
                                                        </div>
                                                    ))}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span style={statusInfo.style}>
                                                        {statusInfo.label}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className={styles.actionsContainer}>
                                                        {can.generate && (
                                                            <button
                                                                className={`${styles.actionButton} ${styles.docsButton}`}
                                                                onClick={() => openDocumentsModal(order)}
                                                                title={t('pages.productionOrder.documents.emitDocuments')}
                                                            >
                                                                <FiFileText size={14} />
                                                            </button>
                                                        )}
                                                        {can.delete && (
                                                            <button
                                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                                disabled={isCancelling}
                                                                onClick={() => showConfirm(
                                                                    `${t('pages.productionOrder.deleteConfirm', { opCode: order.opCode })} ${order.status === 'active' ? t('pages.productionOrder.deleteStockRevert') : ''}`,
                                                                    () => deleteOrder(order.id)
                                                                )}
                                                                title={t('common.delete')}
                                                            >
                                                                {isCancelling ? <FiRefreshCw size={14} className={styles.buttonSpinner} /> : <FiTrash2 size={14} />}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.createModalContent}>
                        {isLoadingStock || isLoadingAux ? (
                            <TableSkeleton rows={3} columns={3} />
                        ) : (
                            <>
                                {/* Header */}
                                <div className={styles.modalHeader}>
                                    <h2><FiPlusCircle size={24} style={{ marginRight: '10px' }} /> {t('pages.productionOrder.newOrder')}</h2>
                                    {tempItems.length > 0 && (
                                        <button className={styles.exportButton} onClick={handleExportToExcel}>
                                            <FiFileText size={18} /> {t('pages.productionOrder.exportToExcel')}
                                        </button>
                                    )}
                                </div>

                                {/* Add-product row */}
                                <div className={styles.addProductRow}>
                                    <div className={styles.productSelect} style={{ flex: 2 }}>
                                        <Select
                                            options={productOptions}
                                            value={selectedProduct}
                                            onChange={handleProductSelect}
                                            placeholder={t('pages.productionOrder.modalSelectProduct')}
                                            isClearable
                                        />
                                    </div>
                                    <select
                                        value={calcMode}
                                        onChange={e => setCalcMode(e.target.value)}
                                        className={styles.select}
                                        style={{ flex: '0 0 140px' }}
                                    >
                                        <option value="mass">{t('pages.productionOrder.modalMass')}</option>
                                        <option value="units">{t('pages.productionOrder.modalUnits')}</option>
                                    </select>
                                    <input
                                        ref={qtyInputRef}
                                        type="number"
                                        className={styles.input}
                                        style={{ flex: '0 0 100px' }}
                                        value={inputValue}
                                        onChange={e => setInputValue(e.target.value)}
                                        onKeyDown={handleQtyKeyDown}
                                        placeholder={t('pages.productionOrder.modalQty')}
                                        min="0"
                                        step="any"
                                    />
                                    <button
                                        onClick={addItemToTempOrder}
                                        className={styles.addButton}
                                        disabled={!selectedProduct || !inputValue}
                                    >
                                        <FiPlus size={18} /> {t('common.add')}
                                    </button>
                                </div>

                                {/* Temp items list */}
                                {tempItems.length > 0 ? (
                                    <div className={styles.tempItemsList}>
                                        {tempItems.map(item => {
                                            const stockStatus = orderStockStatus[item.tempId];
                                            const hasStock = stockStatus?.hasSufficientStock || false;
                                            return (
                                                <div key={item.tempId} className={styles.tempItemRow}>
                                                    <div className={styles.tempItemInfo}>
                                                        <span className={styles.tempItemName}>{item.commercialName}</span>
                                                        <span className={styles.tempItemQty}>
                                                            {item.requestedValue} {item.mode === 'mass' ? 'Kg' : 'Un'}
                                                        </span>
                                                        <span style={{
                                                            marginLeft: '10px',
                                                            fontSize: '0.75rem',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            backgroundColor: hasStock ? '#d4edda' : '#f8d7da',
                                                            color: hasStock ? '#155724' : '#721c24'
                                                        }}>
                                                            {hasStock ? <FiCheck size={12} style={{ marginRight: '4px' }} /> : <FiAlertCircle size={12} style={{ marginRight: '4px' }} />}
                                                            {hasStock ? t('pages.productionOrder.stockOk') : t('pages.productionOrder.stockMissing', { count: stockStatus?.missingMaterials?.length || 0 })}
                                                        </span>
                                                    </div>
                                                    <div className={styles.tempItemActions}>
                                                        <button
                                                            className={styles.simButton}
                                                            onClick={() => setSimulatingItem(item)}
                                                        >
                                                            <FiEye size={14} style={{ marginRight: '4px' }} /> {t('pages.productionOrder.viewStock')}
                                                        </button>
                                                        <button
                                                            className={styles.removeButton}
                                                            onClick={() => setTempItems(prev =>
                                                                prev.filter(item => item.tempId !== item.tempId)
                                                            )}
                                                        >
                                                            <FiTrash2 size={14} /> {t('common.remove')}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className={styles.tempItemsList}>
                                        <EmptyState
                                            title={t('pages.productionOrder.addProductsToOrder')}
                                            message={t('pages.productionOrder.addProductsToOrderMessage')}
                                        />
                                    </div>
                                )}

                                {/* Stock summary - with water material styling */}
                                {tempItems.length > 0 && (
                                    <div className={styles.stockSummarySection}>
                                        <h3><FiTrendingUp size={18} style={{ marginRight: '8px' }} /> {t('pages.productionOrder.stockSummary')}</h3>

                                        {Object.entries(groupedMaterials).map(([key, group]) => (
                                            <div key={key} style={{ marginBottom: '10px' }}>
                                                <button
                                                    className={styles.sectionToggle}
                                                    onClick={() => toggleSection(key)}
                                                >
                                                    <span>{group.label} ({group.items.length})</span>
                                                    <span>{openSections[key] ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}</span>
                                                </button>

                                                {openSections[key] && group.items.length > 0 && (
                                                    <table className={styles.stockTable}>
                                                        <thead>
                                                            <tr>
                                                                <th>{t('pages.productionOrder.material')}</th>
                                                                <th>{t('pages.productionOrder.totalNeeded')}</th>
                                                                <th>{t('pages.productionOrder.availableStock')}</th>
                                                                <th>{t('pages.productionOrder.status')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {group.items.map((mat, idx) => (
                                                                <tr key={idx} className={getMaterialRowClass(mat.isWater)}>
                                                                    <td>
                                                                        {mat.name}
                                                                        {mat.isWater && (
                                                                            <span style={{
                                                                                marginLeft: '8px',
                                                                                fontSize: '0.7rem',
                                                                                padding: '2px 6px',
                                                                                backgroundColor: '#e3f2fd',
                                                                                borderRadius: '12px',
                                                                                color: '#1976d2'
                                                                            }}>
                                                                                <FiDroplet size={12} style={{ marginRight: '4px' }} /> {t('pages.productionOrder.infiniteResource')}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td>{mat.needed.toFixed(3)} {mat.unit}</td>
                                                                    <td>{mat.isWater ? '♾️ Ilimitado' : `${mat.stock.toFixed(3)} ${mat.unit}`}</td>
                                                                    <td className={mat.needed > mat.stock && !mat.isWater ? styles.stockStatusMissing : styles.stockStatusOk}>
                                                                        {mat.isWater ? <FiCheck size={14} style={{ color: '#28a745' }} /> : (mat.needed > mat.stock ? <FiAlertCircle size={14} style={{ color: '#dc3545' }} /> : <FiCheck size={14} style={{ color: '#28a745' }} />)}
                                                                        {mat.isWater ? `${t('pages.productionOrder.ok')} (${t('pages.productionOrder.water')})` : (mat.needed > mat.stock ? t('pages.productionOrder.missing') : t('pages.productionOrder.ok'))}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Footer actions */}
                                <div className={styles.formActions}>
                                    <button onClick={() => setShowCreateModal(false)} className={styles.cancelButton}>
                                        <FiX size={18} /> {t('common.back')}
                                    </button>
                                    <button
                                        onClick={openConfirmModal}
                                        disabled={tempItems.length === 0}
                                        className={styles.confirmButton}
                                    >
                                        <FiCheck size={18} /> {t('pages.productionOrder.confirmOrders')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Individual Simulation Modal - with water material styling */}
            {simulatingItem && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.simModalContent}>
                        <h3><FiEye size={20} style={{ marginRight: '10px' }} /> {t('pages.productionOrder.detailedComposition')} {simulatingItem.commercialName}</h3>
                        <p style={{ fontSize: '0.9rem', color: '#666', margin: '4px 0 0' }}>
                            {t('pages.productionOrder.lossFactorApplied')} <strong>{simulatingItem.lossIndex || 1}</strong>
                        </p>

                        {(() => {
                            const stockStatus = orderStockStatus[simulatingItem.tempId];
                            if (stockStatus && !stockStatus.hasSufficientStock) {
                                return (
                                    <div style={{
                                        backgroundColor: '#f8d7da',
                                        border: '1px solid #f5c6cb',
                                        borderRadius: '4px',
                                        padding: '8px',
                                        margin: '10px 0',
                                        fontSize: '0.85rem',
                                        color: '#721c24'
                                    }}>
                                        <strong><FiAlertCircle size={16} style={{ marginRight: '6px' }} /> {t('pages.productionOrder.attention')}:</strong> {t('pages.productionOrder.insufficientStockFor')} {simulatingItem.commercialName}
                                        {stockStatus.missingMaterials.map((m, idx) => (
                                            <div key={idx}>• {m.name}: {t('pages.productionOrder.needs')} {m.needed.toFixed(3)} {m.unit}, {t('pages.productionOrder.available')} {m.available.toFixed(3)} {m.unit}</div>
                                        ))}
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t('pages.productionOrder.component')}</th>
                                        <th>{t('pages.productionOrder.calculatedQty')}</th>
                                        <th>{t('pages.productionOrder.stock')}</th>
                                        <th>{t('pages.productionOrder.status')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const sim = getSimulation(simulatingItem);
                                        const allSimItems = [...sim.massItems, ...sim.unitItems];
                                        return allSimItems.map((material, idx) => {
                                            const hasStock = material.isWater || material.needed <= material.stockActual;
                                            return (
                                                <tr
                                                    key={idx}
                                                    className={`${styles.tableRow} ${getMaterialRowClass(material.isWater)}`}
                                                    style={!hasStock && !material.isWater ?
                                                        { color: 'var(--danger-color, #e74c3c)' } : {}}
                                                >
                                                    <td>
                                                        {material.displayName || material.inciName || material.label}
                                                        {material.isWater && (
                                                            <span style={{
                                                                marginLeft: '8px',
                                                                fontSize: '0.7rem',
                                                                padding: '2px 6px',
                                                                backgroundColor: '#e3f2fd',
                                                                borderRadius: '12px',
                                                                color: '#1976d2'
                                                            }}>
                                                                <FiDroplet size={12} style={{ marginRight: '4px' }} /> {t('pages.productionOrder.water')}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{material.needed.toFixed(3)} {material.unit}</td>
                                                    <td>{material.isWater ? '♾️ Ilimitado' : material.stockActual.toFixed(3)}</td>
                                                    <td style={{ color: hasStock ? '#27ae60' : '#e74c3c' }}>
                                                        {hasStock ? <FiCheck size={14} style={{ color: '#27ae60' }} /> : <FiAlertCircle size={14} style={{ color: '#e74c3c' }} />}
                                                        {hasStock ? t('pages.productionOrder.ok') : t('pages.productionOrder.insufficient')}
                                                    </td>
                                                </tr>
                                            );
                                        });
                                    })()}
                                </tbody>
                            </table>
                        </div>

                        <button
                            onClick={() => setSimulatingItem(null)}
                            className={styles.addButton}
                            style={{ marginTop: '20px', width: '100%' }}
                        >
                            <FiX size={18} /> {t('common.close')}
                        </button>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.confirmModalContent}>
                        <h2><FiCheckCircle size={24} style={{ marginRight: '10px', color: '#28a745' }} /> {t('pages.productionOrder.confirmOrderTitle')}</h2>
                        <p style={{ color: '#666', fontSize: '0.85rem', margin: '4px 0 12px' }}>
                            {t('pages.productionOrder.confirmOrderSubtitle')}
                        </p>

                        <div style={{
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '6px',
                            padding: '12px',
                            marginBottom: '16px',
                            fontSize: '0.85rem',
                            color: '#856404'
                        }}>
                            <FiInfo size={16} style={{ marginRight: '8px' }} />
                            {t('pages.productionOrder.orderStatusInfo')}
                            <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                                <li><FiCheck size={12} style={{ color: '#28a745', marginRight: '6px' }} /> {t('pages.productionOrder.sufficientStock')}</li>
                                <li><FiClock size={12} style={{ color: '#f39c12', marginRight: '6px' }} /> {t('pages.productionOrder.insufficientStock')}</li>
                            </ul>
                            <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '0.8rem' }}>
                                <FiSettings size={12} style={{ marginRight: '6px' }} /> {t('pages.productionOrder.stockDeductionNote')}
                            </p>
                        </div>

                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t('pages.productionOrder.confirmProduct')}</th>
                                        <th>{t('pages.productionOrder.confirmQty')}</th>
                                        <th>{t('pages.productionOrder.confirmStockStatus')}</th>
                                        <th style={{ width: '130px' }}>{t('pages.productionOrder.confirmObs')}</th>
                                        <th style={{ width: '110px' }}>{t('pages.productionOrder.confirmWaterPct')}</th>
                                        <th style={{ width: '130px' }}>{t('pages.productionOrder.confirmBatch')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tempItems.map(item => {
                                        const row = confirmRows[item.tempId] || {};
                                        const stockStatus = orderStockStatus[item.tempId];
                                        const hasStock = stockStatus?.hasSufficientStock || false;
                                        return (
                                            <tr key={item.tempId} className={styles.tableRow}>
                                                <td>
                                                    <strong>{item.commercialName}</strong>
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        color: 'var(--secondary-color)',
                                                        marginTop: '2px'
                                                    }}>
                                                        {item.code}
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    {item.requestedValue} {item.mode === 'mass' ? 'Kg' : 'Un'}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        backgroundColor: hasStock ? '#d4edda' : '#f8d7da',
                                                        color: hasStock ? '#155724' : '#721c24'
                                                    }}>
                                                        {hasStock ? <FiCheck size={12} style={{ marginRight: '4px' }} /> : <FiAlertCircle size={12} style={{ marginRight: '4px' }} />}
                                                        {hasStock ? t('pages.productionOrder.stockOk') : t('pages.productionOrder.stockMissing', { count: stockStatus?.missingMaterials?.length || 0 })}
                                                    </span>
                                                    {!hasStock && stockStatus?.missingMaterials && (
                                                        <div style={{ fontSize: '0.7rem', marginTop: '4px', color: '#e74c3c' }}>
                                                            {stockStatus.missingMaterials.slice(0, 2).map(m => m.name).join(', ')}
                                                            {stockStatus.missingMaterials.length > 2 && '...'}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <textarea
                                                        className={styles.input}
                                                        value={row.obs || ''}
                                                        onChange={e => handleConfirmRowChange(item.tempId, 'obs', e.target.value)}
                                                        rows={2}
                                                        placeholder={t('pages.productionOrder.confirmObs')}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className={styles.input}
                                                        min="0"
                                                        max="100"
                                                        step="0.1"
                                                        value={row.waterPct || ''}
                                                        onChange={e => handleConfirmRowChange(item.tempId, 'waterPct', e.target.value)}
                                                        placeholder="0.0"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className={styles.input}
                                                        value={row.lote || ''}
                                                        onChange={e => handleConfirmRowChange(item.tempId, 'lote', e.target.value)}
                                                        placeholder={t('pages.productionOrder.confirmBatch')}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.formActions}>
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className={styles.cancelButton}
                                disabled={isFinalizingOrder}
                            >
                                <FiX size={18} /> {t('common.back')}
                            </button>
                            <button
                                onClick={finalizeOrder}
                                className={styles.confirmButton}
                                disabled={isFinalizingOrder}
                            >
                                {isFinalizingOrder ? <><FiRefreshCw size={18} className={styles.buttonSpinner} /> {t('pages.productionOrder.processing')}</> : <><FiCheck size={18} /> {t('pages.productionOrder.confirmOrders')}</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Documents Modal */}
            {showDocumentsModal && selectedOrderForDocs && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.documentsModalContent} style={{
                        maxWidth: '500px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '24px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                    }}>
                        <h2 style={{ margin: '0 0 8px 0', color: '#333' }}>
                            <FiFileText size={24} style={{ marginRight: '10px' }} /> {t('pages.productionOrder.documents.title')} {selectedOrderForDocs.opCode}
                        </h2>

                        <div style={{
                            marginBottom: '16px',
                            padding: '12px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px',
                            border: '1px solid #dee2e6'
                        }}>
                            <div style={{ fontSize: '0.9rem', color: '#495057' }}>
                                <strong><FiPackage size={14} style={{ marginRight: '6px' }} /> {t('pages.productionOrder.documents.product')}</strong>
                                {selectedOrderForDocs.items?.map((item, i) => (
                                    <div key={i} style={{ marginTop: '4px' }}>
                                        • {item.productCode} - {item.productName}
                                        <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                                            ({t('pages.productionOrder.documents.batch')}: {item.lote || 'N/A'})
                                        </span>
                                    </div>
                                ))}
                            </div>
                            {selectedOrderForDocs.status === 'awaiting-mp' && (
                                <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#f39c12' }}>
                                    <FiAlertCircle size={12} style={{ marginRight: '4px' }} /> {t('pages.productionOrder.documents.awaitingMP')}
                                </div>
                            )}
                            {selectedOrderForDocs.status === 'awaiting-start' && (
                                <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#3498db' }}>
                                    <FiInfo size={12} style={{ marginRight: '4px' }} /> {t('pages.productionOrder.documents.awaitingStart')}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <button
                                onClick={() => handleDocumentExport('processo-manipulacao')}
                                style={{
                                    padding: '14px 18px',
                                    textAlign: 'left',
                                    backgroundColor: '#8e44ad',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.95rem',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#6c3483';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#8e44ad';
                                }}
                            >
                                <span><FiFileText size={18} style={{ marginRight: '10px' }} /> {t('pages.productionOrder.documents.processManipulation')}</span>
                                <FaChevronRight size={16} />
                            </button>

                            <button
                                onClick={() => handleDocumentExport('ficha-pesagem')}
                                style={{
                                    padding: '14px 18px',
                                    textAlign: 'left',
                                    backgroundColor: '#2980b9',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.95rem',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#1a5276';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#2980b9';
                                }}
                            >
                                <span><FiFileText size={18} style={{ marginRight: '10px' }} /> {t('pages.productionOrder.documents.processPackaging')}</span>
                                <FaChevronRight size={16} />
                            </button>

                            <button
                                onClick={() => handleDocumentExport('processo-controle-qualidade')}
                                style={{
                                    padding: '14px 18px',
                                    textAlign: 'left',
                                    backgroundColor: '#27ae60',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.95rem',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#1a7a3a';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#27ae60';
                                }}
                            >
                                <span><FiFileText size={18} style={{ marginRight: '10px' }} /> {t('pages.productionOrder.documents.processQuality')}</span>
                                <FaChevronRight size={16} />
                            </button>
                        </div>

                        <div style={{ marginTop: '20px' }}>
                            <button
                                onClick={() => setShowDocumentsModal(false)}
                                style={{
                                    padding: '12px',
                                    width: '100%',
                                    backgroundColor: '#e74c3c',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.95rem',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#c0392b'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e74c3c'}
                            >
                                {t('pages.productionOrder.documents.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductionOrderPage;